import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Headset, Monitor, PlugZap, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { EmptyState, Page, PageBody, PageHeader, Panel } from '@/ui/Panel';
import { Callout } from '@/ui/Callout';
import { Badge, Code } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { api } from '@/lib/api/client';
import { useAtLeast } from '@/lib/auth';
import { cn } from '@/lib/cn';
import { useShortcutHint } from '@/app/StatusLine';
import { countLabel } from '@/lib/format';
import type { DepartmentListResponse, DepartmentSummary } from '@/lib/api/types';
import { RequestCard, RespondedRow } from '@/features/assist/RequestCard';
import {
  useAssistSocket,
  useTick,
  type AssistPhase,
} from '@/features/assist/useAssistSocket';
import type {
  AssistRequestRecord,
  AssistResponseFrame,
} from '@/features/assist/protocol';

/**
 * Assist — the lane's call button.
 *
 * A cashier presses Ctrl+H with a customer in front of them and waits. Every
 * design decision here follows from that: the queue is sorted oldest-first
 * because the longest wait is the most urgent, the answer form is already open
 * on the card, and the connection state is reported literally rather than
 * implied. If this screen is not connected it says so in a way you cannot miss,
 * because the failure mode that costs a store real money is an office that
 * thinks it is listening and is not.
 *
 * Protocol and its verification live in `features/assist/protocol.ts`.
 */

export const Route = createFileRoute('/_app/assist')({
  staticData: { crumb: 'Assist' },
  component: AssistScreen,
});

const PHASE: Record<
  AssistPhase,
  { label: string; tone: 'neutral' | 'positive' | 'negative' | 'warn' }
> = {
  idle: { label: 'Idle', tone: 'neutral' },
  connecting: { label: 'Connecting', tone: 'warn' },
  authenticating: { label: 'Authenticating', tone: 'warn' },
  ready: { label: 'Live', tone: 'positive' },
  disconnected: { label: 'Disconnected', tone: 'negative' },
  error: { label: 'Error', tone: 'negative' },
};

function AssistScreen() {
  // The hub itself refuses anyone below supervisor (routes.ts:132). Checking
  // here too means we show a sentence instead of an unexplained dead socket.
  const canAssist = useAtLeast('supervisor');

  useShortcutHint([
    { keys: '↵', label: 'Send answer' },
    { keys: 'R', label: 'Reconnect' },
  ]);

  const requestsRef = useRef<AssistRequestRecord[]>([]);

  const onResult = useCallback(
    (result: { requestId: string; ok: boolean; error: string | null }) => {
      const row = requestsRef.current.find((r) => r.requestId === result.requestId);
      const where = row ? ` to ${row.registerName}` : '';
      if (result.ok) toast.success(`Answer sent${where}`);
      else toast.error(result.error ?? 'The lane did not accept that answer.');
    },
    []
  );

  const { connection, registers, requests, respond } = useAssistSocket({
    enabled: canAssist,
    onResult,
  });
  // Kept in a ref so the result toast can name the lane without the socket
  // hook having to re-subscribe on every queue change.
  useEffect(() => {
    requestsRef.current = requests;
  }, [requests]);

  // Wait times drive the ordering, so they have to age on their own.
  const now = useTick(5_000);

  const departments = useQuery({
    queryKey: ['departments', { pageSize: 500 }],
    queryFn: () =>
      api.get<DepartmentListResponse>('/departments', { params: { pageSize: 500 } }),
    staleTime: 5 * 60_000,
    enabled: canAssist,
  });

  const departmentOptions: DepartmentSummary[] = useMemo(() => {
    const rows = (departments.data?.data ?? []).filter((d) => d.active !== false);
    return rows.sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name)
    );
  }, [departments.data]);

  const pending = useMemo(
    () =>
      requests
        .filter((r) => !r.resolved)
        // Oldest first: the customer who has waited longest is the emergency.
        .sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp)),
    [requests]
  );

  const responded = useMemo(
    () =>
      requests
        .filter((r) => r.resolved)
        .sort(
          (a, b) =>
            Date.parse(b.resolvedAt ?? b.timestamp) -
            Date.parse(a.resolvedAt ?? a.timestamp)
        ),
    [requests]
  );

  const send = useCallback(
    (frame: AssistResponseFrame) => {
      if (!respond(frame)) {
        toast.error('Not connected to the assist hub — reconnect first.');
      }
    },
    [respond]
  );

  // `R` forces a reconnect from anywhere on the screen except a text field.
  const { reconnect } = connection;
  useEffect(() => {
    if (!canAssist) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (event.key.toLowerCase() === 'r') {
        event.preventDefault();
        reconnect();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [canAssist, reconnect]);

  const phase = PHASE[connection.phase];
  const live = connection.phase === 'ready';

  if (!canAssist) {
    return (
      <Page>
        <PageHeader eyebrow="Selling" title="Assist" />
        <PageBody>
          <Callout tone="warn" title="Supervisor access required" icon={<ShieldAlert />}>
            Answering a lane needs the supervisor role or above. Ask a manager to raise
            your role, or have them take the request.
          </Callout>
        </PageBody>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        eyebrow="Selling"
        title="Assist"
        subtitle="Live help for the lanes — a cashier is waiting on every line in this queue."
        actions={
          <>
            <Badge tone={phase.tone} dot pulse={!live}>
              {phase.label}
              {connection.retryIn !== null ? ` · retry ${connection.retryIn}s` : ''}
            </Badge>
            <Button
              size="sm"
              icon={<PlugZap className="size-3.5" />}
              onClick={reconnect}
              kbd="R"
            >
              Reconnect
            </Button>
          </>
        }
      />
      <PageBody className="flex flex-col gap-3">
        {live ? null : (
          <Callout
            tone={connection.phase === 'error' ? 'negative' : 'warn'}
            title={
              connection.phase === 'connecting' || connection.phase === 'authenticating'
                ? 'Opening the link to the lanes…'
                : 'Not listening to the lanes'
            }
            icon={<PlugZap />}
            actions={
              <Button size="xs" onClick={reconnect}>
                Reconnect
              </Button>
            }
          >
            {connection.detail ?? 'The assist socket is not established yet.'}{' '}
            {connection.phase === 'disconnected' && connection.retryIn !== null
              ? `Retrying automatically in ${connection.retryIn}s (attempt ${connection.attempts + 1}).`
              : null}
            {connection.phase === 'error'
              ? ' Automatic retries are off for this failure — fix it, then reconnect.'
              : null}
            <span className="text-2xs text-ink-faint block pt-1">
              Requests raised while this is down are still queued on the server and appear
              on reconnect.
            </span>
          </Callout>
        )}

        <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="flex min-w-0 flex-col gap-3">
            <Panel
              title="Waiting"
              description={
                pending.length ? countLabel(pending.length, 'request') : 'Nothing waiting'
              }
              bleed
              bodyClassName="p-2"
            >
              {pending.length === 0 ? (
                <EmptyState
                  compact
                  icon={<Headset />}
                  title={live ? 'No one is waiting' : 'Queue unknown'}
                  description={
                    live
                      ? 'Requests appear the moment a cashier presses Ctrl+H at a lane.'
                      : 'The queue cannot be read while the link is down.'
                  }
                />
              ) : (
                <ul className="flex flex-col gap-2">
                  {pending.map((request, index) => (
                    <RequestCard
                      key={request.requestId}
                      request={request}
                      departments={departmentOptions}
                      now={now}
                      onRespond={send}
                      autoFocus={index === 0}
                    />
                  ))}
                </ul>
              )}
            </Panel>

            <Panel
              title="Responded"
              description="Last 50 requests the hub is holding, newest first"
              bleed
            >
              {responded.length === 0 ? (
                <EmptyState compact title="Nothing answered yet this session" />
              ) : (
                <ul className="flex flex-col">
                  {responded.map((request) => (
                    <RespondedRow key={request.requestId} request={request} />
                  ))}
                </ul>
              )}
            </Panel>
          </div>

          <Panel
            title="Registers on the hub"
            description={
              live ? countLabel(registers.length, 'lane') : 'Unknown while disconnected'
            }
            bleed
            className="self-start"
          >
            {registers.length === 0 ? (
              <EmptyState
                compact
                icon={<Monitor />}
                title={live ? 'No lanes connected' : 'Not connected'}
                description={
                  live
                    ? 'A register appears here only while its own assist socket is up.'
                    : undefined
                }
              />
            ) : (
              <ul className="flex flex-col">
                {registers.map((register) => (
                  <li
                    key={register.registerId}
                    className="border-line-soft flex items-center gap-2 border-b px-3 py-1.5 last:border-0"
                  >
                    {/* Presence is only true while we are connected — once the
                        socket drops this list is a memory, and it says so. */}
                    <Badge tone={live ? 'positive' : 'neutral'} dot pulse={live}>
                      {live ? 'Online' : 'Stale'}
                    </Badge>
                    <span
                      className={cn(
                        'truncate text-xs',
                        live ? 'text-ink' : 'text-ink-faint'
                      )}
                    >
                      {register.registerName}
                    </span>
                    <Code className="ml-auto">#{register.registerId}</Code>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </PageBody>
    </Page>
  );
}
